import { Box, StripeWrapper } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { LoaderFunction, MetaFunction } from "react-router";
import { data as responseData } from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import { useClientContext } from "~/contexts/client-context";
import { getSpecialOffers } from "~/core/deprecated/special-offers";
import { prepareSeoData } from "~/core/transformers/seo.transformer";
import {
  MainBanner,
  MainBannerTranslationProvider,
} from "~/features/main-banner";
import { PromoPageSteps } from "~/features-deprecated/promoPageSteps/PromoPageSteps";
import { SpecialOffers } from "~/features-deprecated/special-offers/SpecialOffers";
import { WelcomeOffer } from "~/features-deprecated/welcome-offer/WelcomeOffer";
import { Club, Gift, UserCircle } from "~/icons";
import {
  featureFlagsContext,
  sdkContext,
  settingsContext,
} from "~/server/get-load-context";
import { usePlayNowUrl } from "~/shared/utils/aff-integration";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { PageConstructor } from "~/widgets/page-constructor";
import { loadPagesByConstructor } from "~/widgets/pages-by-constructor/page-by-constructor.server";
import { SupportMedia } from "~/widgets/support/SupportMedia";
import { SupportMessengers } from "~/widgets/support/SupportMessengers";

import type { Route } from "./+types/$lang._index";

const createHomeValidator = (): ObjectSchema<{
  lang: string;
}> => {
  return object({
    lang: string().required(),
  });
};

export const loader = (async ({ context, params, request }) => {
  const settings = context.get(settingsContext);
  const sdk = context.get(sdkContext);
  const featureFlags = context.get(featureFlagsContext);

  const { lang } = createHomeValidator().validateSync(params);
  const homeSlug =
    typeof settings?.app?.home?.slug === "string"
      ? settings.app.home.slug
      : undefined;
  const isHomeConstructorPageEnabled = featureFlags.getItem(
    "homePageConstructor",
  ).enabled;

  if (isHomeConstructorPageEnabled && !homeSlug) {
    throw new Response(
      JSON.stringify({
        message: "Home page not found",
      }),
      {
        status: 404,
        statusText: "Not Found",
      },
    );
  }

  const pagesConstructorData = homeSlug
    ? await loadPagesByConstructor(sdk, homeSlug, lang, {
        slug: homeSlug ?? "",
        segment: settings?.app?.home?.segment,
      })
    : undefined;

  return responseData(
    {
      specialOffers: getSpecialOffers(),
      featureFlags: {
        isSpecialOffersButtonEnabled: featureFlags.getItem(
          "specialOffersButton",
        ).enabled,
        isSupportIconsEnabled:
          featureFlags.getItem("supportIconsBlock").enabled,
        isSocialMediaIconsEnabled:
          featureFlags.getItem("socialMediaIcons").enabled,
      },
      pageData: pagesConstructorData?.page,
      pages: pagesConstructorData?.pages,
      settings,
      originalUrl: getOriginalUrl(request.url),
      languages: settings?.languages,
      isHomeConstructorPageEnabled,
    },
    {
      headers: {
        "Cache-Control": "public, max-age=300",
      },
    },
  );
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  return prepareSeoData({
    pageSeo: data?.pageData?.page.seo,
    fallbackSeo: data?.pageData?.fallbackSeo?.seo,
    originalUrl: `${data?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: data?.languages ?? [],
  });
};

const Page: FC<Route.ComponentProps> = function ({
  loaderData: {
    specialOffers,
    featureFlags,
    pageData,
    isHomeConstructorPageEnabled,
    pages,
  },
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation("home");
  const { t: mainBannerTranslation } = useTranslation("mainBanner");

  const welcomeOfferBonuses = [
    {
      title: t("welcome-offer.first-bonus.title"),
      subtitle: t("welcome-offer.first-bonus.subtitle"),
    },
    {
      title: t("welcome-offer.second-bonus.title"),
      subtitle: t("welcome-offer.second-bonus.subtitle"),
    },
  ];
  const playNowUrl = usePlayNowUrl();
  const data = [
    {
      icon: <Gift viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />,
      text: {
        en: "Instantly available bonuses",
        zh: "扑克界最优的欢迎优惠！",
      },
    },
    {
      icon: <Club viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />,
      text: {
        en: "Advanced protection",
        zh: "采用最先进的保护措施，\n" + "确保游戏公平公正！",
      },
    },
    {
      icon: (
        <UserCircle viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />
      ),
      text: {
        en: "Superfast registration",
        zh: "超快速注册，\n" + "立即开始游戏！",
      },
    },
  ];

  const { playerID } = useClientContext();
  const { gtm } = useGtmContext();

  return (
    <>
      {pageData && isHomeConstructorPageEnabled ? (
        <PageConstructor data={pageData} pages={pages} />
      ) : (
        <div className="main-page">
          <MainBannerTranslationProvider
            value={{
              heading: {
                title: mainBannerTranslation("topper.heading.title"),
              },
              buttons: {
                create: mainBannerTranslation("topper.button.create"),
                login: mainBannerTranslation("topper.button.login"),
              },
            }}
          >
            <Box
              bg={"/main-page-bg-mobile.jpg"}
              md={{
                bg: "/main-page-bg-desktop.jpg",
              }}
            >
              <MainBanner
                data={data}
                playNowLink={playNowUrl}
                language={language}
                analytics={{
                  createAccountMain() {
                    gtm.createAccountMain({
                      eventTime: new Date(),
                      playerID,
                      screenName: "mainPage",
                    });
                  },
                  logInMain() {
                    gtm.logInMain({
                      eventTime: new Date(),
                      playerID,
                      screenName: "mainPage",
                    });
                  },
                }}
              />
            </Box>
          </MainBannerTranslationProvider>
          <StripeWrapper variant="default">
            <PromoPageSteps
              firstStepText={t("steps.text.registration")}
              secondStepText={t("steps.text.game")}
              thirdStepText={t("steps.text.rewards")}
              playNowText={t("steps.button.play")}
              playNowLink={playNowUrl}
              analytics={{
                playNow() {
                  gtm.playNow({
                    eventTime: new Date(),
                    playerID,
                    screenName: "mainPage",
                  });
                },
              }}
            />
          </StripeWrapper>
          <WelcomeOffer
            title={t("welcome-offer.heading.title")}
            guaranteed={{
              cards: welcomeOfferBonuses,
            }}
            surpriseBonusCard={{
              title: t("welcome-offer.third-bonus.title"),
              description: t("welcome-offer.third-bonus.subtitle"),
            }}
            image={{
              imageVariants: "/happy-man-icon.png",
            }}
            background={{
              imageVariants: "/welcome-offer-bg-desktop.jpg",
              mobileImageVariants: "/welcome-offer-bg-mobile.jpg",
            }}
          />
          <SpecialOffers
            featureFlags={featureFlags}
            specialOffers={specialOffers}
          />
          {featureFlags.isSupportIconsEnabled && <SupportMessengers />}
          {featureFlags.isSocialMediaIconsEnabled && <SupportMedia />}
        </div>
      )}
    </>
  );
};

export default Page;
